/**
 * CyberSells Purchase Handler
 * Handles purchase events, awards XP, checks achievements, and updates player data
 */

(function() {
  'use strict';
  
  // Track if page view has been tracked to prevent duplicates
  let _pageViewTracked = false;

  /**
   * Handle a purchase event
   * @param {Object} purchaseData - Purchase information
   * @param {number} purchaseData.totalPrice - Total price in cents
   * @param {Array} purchaseData.items - Array of purchased items
   * @param {string} purchaseData.orderId - Order ID
   */
  function handlePurchase(purchaseData) {
    if (!window.CyberSells_playerData || !window.CyberSells_stats || !window.CyberSells_achievements) {
      if (window.cyberSellsConfig?.debug) {
        console.warn('[CyberSells] Required modules not loaded');
      }
      return;
    }

    if (window.cyberSellsConfig?.debug) {
      console.log('[CyberSells] Processing purchase...', purchaseData);
    }

    // Process post-purchase rewards if module is loaded
    if (window.CyberSells_postPurchase && purchaseData.orderId) {
      const rewards = window.CyberSells_postPurchase.processPurchaseRewards(purchaseData);
      if (window.cyberSellsConfig?.debug) {
        console.log('[CyberSells] Post-purchase rewards processed:', rewards);
      }
      return rewards;
    }

    // Fallback to legacy purchase handling
    // Load player data
    const playerData = window.CyberSells_playerData.loadPlayerData();
    
    
    // Calculate and award XP
    const xpGained = window.CyberSells_stats.calculatePurchaseXp(purchaseData.totalPrice);
    const levelResult = window.CyberSells_stats.awardXp(playerData, xpGained);
    
    // Apply stat changes from purchased items
    const statDeltas = calculateItemStats(purchaseData.items);
    window.CyberSells_playerData.updatePlayerStats(playerData, statDeltas);
    
    // Update purchase tracking
    playerData.purchaseCount++;
    playerData.totalSpent += purchaseData.totalPrice;
    
    // Track purchased items
    if (Array.isArray(purchaseData.items)) {
      purchaseData.items.forEach(item => {
        playerData.itemsPurchased.push({
          id: item.id || item.variant_id,
          title: item.title,
          stats: item.stats || {},
          purchasedAt: Date.now(),
          orderId: purchaseData.orderId
        });
      });
    }
    
    // Check for new achievements
    const newAchievements = window.CyberSells_achievements.checkAchievements(playerData);
    const achievementXp = window.CyberSells_achievements.awardAchievements(playerData, newAchievements);
    
    // Award achievement XP (may cause additional level up)
    if (achievementXp > 0) {
      const achievementLevelResult = window.CyberSells_stats.awardXp(playerData, achievementXp);
      if (achievementLevelResult.leveledUp && !levelResult.leveledUp) {
        // Achievement XP caused level up
        levelResult.leveledUp = true;
        levelResult.newLevel = achievementLevelResult.newLevel;
      }
    }
    
    // Track quest progress (if quest system loaded)
    let completedQuests = [];
    if (window.CyberSells_quests) {
      completedQuests = window.CyberSells_quests.trackQuestEvent('purchase', purchaseData, playerData);
      
      // Track achievement unlock for quests
      if (newAchievements.length > 0) {
        const moreQuests = window.CyberSells_quests.trackQuestEvent('achievement', {}, playerData);
        completedQuests = [...completedQuests, ...moreQuests];
      }
    }
    
    // Save player data
    window.CyberSells_playerData.savePlayerData(playerData);
    
    // Show notifications
    showPurchaseNotifications(xpGained, levelResult, newAchievements, completedQuests);
    
    // Dispatch custom event
    dispatchPurchaseEvent(playerData, levelResult, newAchievements, completedQuests);
    
    if (window.cyberSellsConfig?.debug) {
      console.log('[CyberSells] Purchase processed. New stats:', playerData.stats);
    }
    
    return {
      xpGained,
      levelResult,
      newAchievements,
      completedQuests,
      playerData
    };
  }

  /**
   * Calculate total stat changes from items
   */
  function calculateItemStats(items) {
    const statDeltas = {};
    
    if (!Array.isArray(items)) return statDeltas;
    
    items.forEach(item => {
      const stats = item.stats || {};
      for (const [stat, value] of Object.entries(stats)) {
        statDeltas[stat] = (statDeltas[stat] || 0) + value;
      }
    });
    
    return statDeltas;
  }

  /**
   * Show toast notifications for purchase results
   */
  function showPurchaseNotifications(xpGained, levelResult, newAchievements, completedQuests = []) {
    // Get toast system from CyberSells instance
    const csInstance = window.cyberSells || window.CyberSells;
    if (!csInstance || !csInstance._toastSystem) {
      if (window.cyberSellsConfig?.debug) {
        console.warn('[CyberSells] Toast system not available');
      }
      return;
    }

    const toast = csInstance._toastSystem;
    
    // Show XP gained
    if (xpGained > 0) {
      toast.showXpGained(xpGained, 'Purchase complete!');
    }
    
    // Show level up (after a delay)
    if (levelResult.leveledUp) {
      setTimeout(() => {
        toast.showLevelUp({
          newLevel: levelResult.newLevel,
          oldLevel: levelResult.oldLevel
        });
      }, 500);
    }
    
    // Show achievements (staggered)
    newAchievements.forEach((achievement, index) => {
      setTimeout(() => {
        toast.showAchievement(achievement);
      }, 1000 + (index * 500));
    });
    
    // Show completed quests (staggered after achievements)
    const questDelay = 1000 + (newAchievements.length * 500);
    completedQuests.forEach((quest, index) => {
      setTimeout(() => {
        toast.showToast('achievement', {
          icon: quest.icon,
          title: 'Quest Complete!',
          description: quest.title,
          xpReward: quest.reward.xp
        });
      }, questDelay + (index * 500));
    });
  }

  /**
   * Dispatch custom purchase event
   */
  function dispatchPurchaseEvent(playerData, levelResult, newAchievements, completedQuests = []) {
    const event = new CustomEvent('cybersells:purchase', {
      detail: {
        playerData,
        levelResult,
        newAchievements,
        completedQuests
      }
    });
    document.dispatchEvent(event);
  }

  /**
   * Track product view
   */
  function trackProductView(productId, productTitle) {
    if (!window.CyberSells_playerData) return;
    
    const playerData = window.CyberSells_playerData.loadPlayerData();
    
    // Add to products viewed (limit to last 100)
    if (!playerData.productsViewed.includes(productId)) {
      playerData.productsViewed.push(productId);
      if (playerData.productsViewed.length > 100) {
        playerData.productsViewed.shift();
      }
    }
    
    // Update session data
    if (!playerData.currentSession.productsViewed.includes(productId)) {
      playerData.currentSession.productsViewed.push(productId);
    }
    
    // Track quest progress
    if (window.CyberSells_quests) {
      window.CyberSells_quests.trackQuestEvent('product_view', { productId, productTitle }, playerData);
    }
    
    // Check for exploration achievements
    const newAchievements = window.CyberSells_achievements?.checkAchievements(playerData);
    if (newAchievements && newAchievements.length > 0) {
      const achievementXp = window.CyberSells_achievements.awardAchievements(playerData, newAchievements);
      if (achievementXp > 0) {
        window.CyberSells_stats?.awardXp(playerData, achievementXp);
      }
      
      // Show achievement notifications
      const csInstance = window.cyberSells || window.CyberSells;
      if (csInstance?._toastSystem) {
        newAchievements.forEach((achievement, index) => {
          setTimeout(() => {
            csInstance._toastSystem.showAchievement(achievement);
          }, index * 500);
        });
      }
    }
    
    window.CyberSells_playerData.savePlayerData(playerData);
  }

  /**
   * Track cart add
   */
  function trackCartAdd() {
    if (!window.CyberSells_playerData) return;
    
    const playerData = window.CyberSells_playerData.loadPlayerData();
    playerData.cartAdds++;
    
    // Track quest progress
    if (window.CyberSells_quests) {
      window.CyberSells_quests.trackQuestEvent('cart_add', {}, playerData);
    }
    
    window.CyberSells_playerData.savePlayerData(playerData);
  }

  /**
   * Track page view
   */
  function trackPageView() {
    // Prevent duplicate tracking (idempotent)
    if (_pageViewTracked) return;
    
    if (!window.CyberSells_playerData) return;
    
    const playerData = window.CyberSells_playerData.loadPlayerData();
    playerData.pageViews++;
    playerData.currentSession.pageViews++;
    window.CyberSells_playerData.savePlayerData(playerData);
    
    _pageViewTracked = true;
  }

  // Expose module
  window.CyberSells_purchaseHandler = {
    handlePurchase,
    trackProductView,
    trackCartAdd,
    trackPageView
  };

  // Auto-track page views (only once)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackPageView, { once: true });
  } else {
    trackPageView();
  }

})();
